<template>
  <el-dialog
    :key="dialogShellKey"
    :visible.sync="innerVisible"
    :before-close="handleBeforeClose"
    :custom-class="dialogCustomClass"
    append-to-body
    :close-on-click-modal="false"
    :lock-scroll="true"
    :width="dialogWidth"
    :top="dialogTop"
  >
    <div slot="title" :class="headerClass">
      <template v-if="isReadonly">
        <h2 class="styleFormDialogShell__title">{{ dialogTitle }}</h2>
        <span class="styleFormDialogShell__badge" :class="modeBadgeClass">{{ modeBadgeText }}</span>
      </template>
      <template v-else>
        <div class="tagFormDialogShell__headerLeading">
          <h2 class="styleFormDialogShell__title">{{ dialogTitle }}</h2>
          <p class="tagFormDialogShell__headerHint">
            一级标签为顶层分组；二级标签必须挂在某个一级标签下。
          </p>
        </div>

        <div class="tagFormDialogShell__headerLevel">
          <span class="tagFormDialogShell__headerLevelLabel">标签层级</span>
          <style-form-choice
            v-model="tagLevel"
            :options="tagLevelOptions"
            aria-label="标签层级"
          />
        </div>

        <span class="styleFormDialogShell__badge" :class="modeBadgeClass">{{ modeBadgeText }}</span>
      </template>
    </div>

    <div v-loading="loading" :class="dialogBodyClass">
      <template v-if="isReadonly">
        <el-card class="styleFormCard" shadow="never">
          <div slot="header">
            <strong class="styleFormCard__title">标签信息</strong>
          </div>
          <div class="styleFormReadonlyGrid">
            <div>
              <span class="styleFormReadonlyItem__label">标签层级</span>
              <span class="styleFormReadonlyItem__value">{{ tagLevelOptionLabel }}</span>
            </div>
            <div v-if="isSecondLevelTag" class="styleFormReadonlyGrid__full">
              <span class="styleFormReadonlyItem__label">所属一级标签</span>
              <span class="styleFormReadonlyItem__value">{{ parentTagDisplay }}</span>
            </div>
            <div>
              <span class="styleFormReadonlyItem__label">标签名称</span>
              <span class="styleFormReadonlyItem__value">{{ ruleForm.name || '—' }}</span>
            </div>
            <div>
              <span class="styleFormReadonlyItem__label">标签 Title</span>
              <span class="styleFormReadonlyItem__value">{{ ruleForm.title || '—' }}</span>
            </div>
            <div v-if="ruleForm.updateTime" class="styleFormReadonlyGrid__full">
              <span class="styleFormReadonlyItem__label">更新时间</span>
              <span
                class="styleFormReadonlyItem__value styleFormReadonlyItem__value--mono"
              >{{ ruleForm.updateTime }}</span>
            </div>
          </div>
        </el-card>
      </template>

      <el-form
        v-else
        ref="ruleForm"
        :model="ruleForm"
        :rules="formRules"
        label-position="top"
        hide-required-asterisk
        class="tagFormDialogShell__form"
        :class="{ 'tagFormDialogShell__form--withPicker': isSecondLevel }"
        @submit.native.prevent
      >
        <section class="tagFormDialogShell__metaPanel">
          <div class="tagFormDialogShell__nameRow">
            <div class="styleFormField tagFormDialogShell__field">
              <span class="styleFormField__label styleFormField__label--required">标签名称</span>
              <el-form-item prop="name" label-width="0" class="styleFormField__item">
                <el-input
                  v-model="ruleForm.name"
                  class="tagFormDialogShell__textInput"
                  placeholder="请输入标签名称"
                />
              </el-form-item>
            </div>

            <div class="styleFormField tagFormDialogShell__field">
              <span class="styleFormField__label styleFormField__label--required">标签 Title</span>
              <el-form-item prop="title" label-width="0" class="styleFormField__item">
                <el-input
                  v-model="ruleForm.title"
                  class="tagFormDialogShell__textInput"
                  placeholder="请输入标签 Title"
                />
              </el-form-item>
            </div>
          </div>
        </section>

        <section v-if="isSecondLevel" class="tagFormDialogShell__pickerWorkspace">
          <span class="styleFormField__label styleFormField__label--required tagFormDialogShell__pickerLabel">
            所属一级标签
          </span>
          <el-form-item prop="parentId" label-width="0" class="tagFormDialogShell__pickerItem">
            <tag-parent-card-picker
              ref="parentPicker"
              v-model="ruleForm.parentId"
              full-height
            />
          </el-form-item>
        </section>
      </el-form>
    </div>

    <div slot="footer" class="styleFormDialogShell__footer">
      <template v-if="isReadonly">
        <el-button class="actionButton actionButtonQuery" type="primary" size="small" @click="handleClose">
          关闭
        </el-button>
      </template>
      <template v-else>
        <el-button class="actionButton actionButtonGhost" size="small" @click="handleClose">
          取消
        </el-button>
        <el-button
          class="actionButton actionButtonQuery"
          type="primary"
          size="small"
          @click="handleSubmit"
        >
          保存
        </el-button>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import StyleFormChoice from '../styleManage/StyleFormChoice.vue'
import TagParentCardPicker from './TagParentCardPicker.vue'
import {
  TAG_LEVEL_OPTIONS,
  formatParentTagLabel,
  getTagLevelOptionLabel,
  inferTagLevel,
} from './tagListHelpers'

export default {
  name: 'TagFormDialog',
  components: {
    StyleFormChoice,
    TagParentCardPicker,
  },
  props: {
    visible: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    /** 0 创建，1 查看，2 编辑 */
    mode: { type: Number, default: 0 },
    ruleForm: { type: Object, required: true },
    rules: { type: Object, required: true },
  },
  data() {
    return {
      tagLevel: '1',
      tagLevelOptions: TAG_LEVEL_OPTIONS,
    }
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(next) {
        this.$emit('update:visible', next)
      },
    },
    isReadonly() {
      return this.mode === 1
    },
    /** 由 parentId 推断，查看态与列表层级列一致，避免异步加载后 tagLevel 未同步 */
    isSecondLevelTag() {
      return inferTagLevel(this.ruleForm.parentId) === '2'
    },
    isSecondLevel() {
      return this.tagLevel === '2'
    },
    dialogShellKey() {
      return this.isReadonly ? 'tag-form-readonly' : 'tag-form-edit'
    },
    headerClass() {
      return this.isReadonly
        ? 'styleFormDialogShell__header'
        : 'styleFormDialogShell__header tagFormDialogShell__header'
    },
    dialogCustomClass() {
      const base = 'tagFormDialogShell styleFormDialogShell collectionModule'
      if (this.isReadonly) {
        return `${base} tagFormDialogShell--readonly`
      }
      return `batch-tag-dialog ${base} genericResourceModule`
    },
    dialogWidth() {
      return this.isReadonly ? '580px' : undefined
    },
    dialogTop() {
      return this.isReadonly ? '6vh' : '7vh'
    },
    dialogBodyClass() {
      return this.isReadonly
        ? 'styleFormDialogShell__scroll tagFormDialogShell__readonlyScroll'
        : 'tagFormDialogShell__body'
    },
    dialogTitle() {
      if (this.mode === 0) return '新建标签'
      if (this.mode === 1) return '查看标签'
      return '编辑标签'
    },
    modeBadgeText() {
      if (this.mode === 0) return '新建'
      if (this.mode === 1) return '只读'
      return '编辑中'
    },
    modeBadgeClass() {
      if (this.mode === 0) return 'styleFormDialogShell__badge--create'
      if (this.mode === 1) return 'styleFormDialogShell__badge--view'
      return 'styleFormDialogShell__badge--edit'
    },
    tagLevelOptionLabel() {
      return getTagLevelOptionLabel(this.ruleForm.parentId)
    },
    parentTagDisplay() {
      return formatParentTagLabel(this.ruleForm.parentName, this.ruleForm.parentId)
    },
    formRules() {
      return {
        ...this.rules,
        parentId: [
          {
            validator: (_rule, value, callback) => {
              if (!this.isSecondLevel) {
                callback()
                return
              }
              const parsed = Number(value)
              if (!Number.isFinite(parsed) || parsed <= 0) {
                callback(new Error('请选择所属一级标签'))
                return
              }
              callback()
            },
            trigger: 'change',
          },
        ],
      }
    },
  },
  watch: {
    visible(next) {
      if (next) {
        this.syncTagLevelFromForm()
        this.$nextTick(() => {
          if (!this.isReadonly && this.$refs.ruleForm) {
            this.$refs.ruleForm.clearValidate()
          }
          if (!this.isReadonly && this.$refs.parentPicker) {
            this.$refs.parentPicker.loadGroups()
          }
        })
      }
    },
    mode() {
      if (this.visible) {
        this.syncTagLevelFromForm()
      }
    },
    loading(next, prev) {
      if (prev && !next && this.visible) {
        this.syncTagLevelFromForm()
      }
    },
    'ruleForm.parentId'() {
      if (this.visible) {
        this.syncTagLevelFromForm()
      }
      if (this.$refs.ruleForm && this.isSecondLevel && !this.isReadonly) {
        this.$refs.ruleForm.validateField('parentId')
      }
    },
    tagLevel(next) {
      if (this.isReadonly) {
        return
      }
      // 一级：父级固定为 0。二级：先清成 ''，再由卡片墙选真实一级 id。
      if (next === '1') {
        this.ruleForm.parentId = 0
        return
      }
      if (Number(this.ruleForm.parentId) === 0) {
        this.ruleForm.parentId = ''
      }
    },
  },
  methods: {
    syncTagLevelFromForm() {
      this.tagLevel = inferTagLevel(this.ruleForm.parentId)
    },
    handleBeforeClose(done) {
      this.$emit('close')
      done()
    },
    handleClose() {
      this.$emit('close')
      this.innerVisible = false
    },
    handleSubmit() {
      if (this.isReadonly) {
        return
      }
      const parentPicker = this.$refs.parentPicker
      if (this.isSecondLevel && parentPicker && !parentPicker.hasOptions) {
        this.$message.warning('请先创建一级标签')
        return
      }
      if (!this.$refs.ruleForm) {
        this.$emit('submit')
        return
      }
      this.$refs.ruleForm.validate((valid) => {
        if (valid) {
          this.$emit('submit')
        } else {
          this.$message.info('请完整填写表单')
        }
      })
    },
  },
}
</script>

<style scoped>
.styleFormField__item {
  margin-bottom: 0;
}

.styleFormField__item >>> .el-form-item__content {
  line-height: normal;
}

.styleFormField__item >>> .el-form-item__error {
  position: static;
  margin-top: 6px;
  padding-top: 0;
}

.tagFormDialogShell__headerLevel >>> .styleFormChoice {
  width: 100%;
  max-width: 280px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.tagFormDialogShell__headerLevel >>> .styleFormChoice__btn {
  padding: 8px 12px;
  font-size: 12px;
}
</style>

<style src="../../resourceManage/genericResourceManage/genericResourceDialogShell.css"></style>
